import { useAsyncState, type UseAsyncStateOptions } from '@vueuse/core'
import type { MaybeRef } from 'vue'
import { showToast } from 'vant'
import i18n from '@/i18n'
import { openMusicAuth } from '@/composables/useMusicAuthDialog'

export interface ApiResponse<T = any> {
  code: number
  data?: T
  message?: string
}

export interface RequestError {
  code: number
  message: string
}

/** 解包信封：code===0 → resolve(res.data)；否则 reject({code,message})，默认弹 Toast（silent 跳过） */
export async function unwrapData<T = any>(
  promise: Promise<ApiResponse<T>>,
  options: { silent?: boolean } = {}
): Promise<T> {
  const { silent = false } = options;
  const res = await promise;

  if (res?.code === 0) {
    return res.data as T;
  }

  const err: RequestError = {
    code: res?.code ?? -1,
    message: res?.message || i18n.global.t('requestFailed'),
  };
  // 2901 网易云未绑定：弹授权弹窗，跳过默认错误 Toast
  if (err.code === 2901) {
    openMusicAuth();
    throw err;
  }
  if (!silent) showToast(err.message);
  throw err;
}

export type UseApiOptions = UseAsyncStateOptions<true, any> & { silent?: boolean }

export function useApi<T = any, A extends any[] = any[]>(
  fn: (...args: A) => Promise<ApiResponse<T>>,
  // NoInfer 阻止传入的 null 反向污染泛型 T 的推断（否则 T 会被推断为含 null 的类型，导致消费侧断言异常）
  initialState: NoInfer<T> | null = null,
  options: UseApiOptions = {},
) {
  const { silent = false, ...asyncStateOptions } = options
  // initialState 约定用 null 表示"无初始值"，但 useAsyncState 该参数类型为 MaybeRef<T>（不含 null），运行时等价，故断言对齐
  return useAsyncState<T>(async (...args: A) => unwrapData<T>(fn(...args), { silent }), initialState as MaybeRef<T>, asyncStateOptions)
}
